import React, { CSSProperties, useMemo, useState } from 'react';
import { uniqBy } from 'lodash';
import { Filter, Input, Menu, Select } from 'sugar-design';

import JobManagerSelect, { Option } from '@/pages/headhunters/[orgId]/_lib/JobManagerSelect';

import { JOB_HR } from '@/constants/common';
import { RelatedJob } from '@/models/job';
import { getI18n } from '@/utils/i18n';

import styles from './index.cm.styl';

const i18n = getI18n();

interface Props {
  style: CSSProperties;
  relatedJobEntities: Record<RelatedJob['id'], RelatedJob>;
  onJobFilterChange: (jobId?: RelatedJob['id']) => void;
  value: RelatedJob['id'];
}

const JobFilter = ({ style, relatedJobEntities, onJobFilterChange, value }: Props) => {
  const [jobManagerIds, setJobManagerIds] = useState<Option['managerId'][]>([]);
  const [searchKey, setSearchKey] = useState('');
  const [selectedJobTitle, setSelectedJobTitle] = useState('');

  const jobs = useMemo(() => Object.values(relatedJobEntities), [relatedJobEntities]);

  /**
   * 构建职位下拉列表选项
   */
  const jobFilterOptions = useMemo(
    () =>
      jobs
        .filter((job) => {
          let flag = true;
          if (searchKey) {
            flag = Select.isIncludesKeyword(job.title, searchKey);
          }
          if (jobManagerIds.length) {
            const manager = job.managers?.find((item) => item.role === JOB_HR);

            flag = flag && !!manager && jobManagerIds.includes(manager.id);
          }
          return flag;
        })
        .map((job) => ({
          value: job.id,
          label: job.title,
        })),
    [jobManagerIds, jobs, searchKey]
  );

  const managerFilterOptions = useMemo(
    () =>
      uniqBy(
        jobs
          .map(({ managers }) => {
            const manager = managers?.find((item) => item.role === JOB_HR);
            return {
              managerId: manager?.id,
              managerName: manager?.name,
              managerEmail: manager?.email,
            };
          })
          .filter((option) => !!option.managerId),
        'managerId'
      ),
    [jobs]
  );

  return (
    <Filter
      // eslint-disable-next-line react/forbid-component-props
      style={style}
      isActive={!!value}
      value={value}
      prefix={i18n.t`推荐职位` + (value ? `: ${selectedJobTitle}` : '')}
      customContentClassName={styles.customContent}
      onDropdownOpenChange={(isOpen) => {
        if (!isOpen) {
          setSearchKey('');
          setJobManagerIds([]);
        }
      }}
      customContent={(close) => (
        <div>
          <div className={styles.jobFilters}>
            <Input
              size="md"
              className={styles.jobSearch}
              placeholder={i18n.t`请输入职位名称`}
              onChange={(e) => setSearchKey(e.target.value)}
            />
            <JobManagerSelect
              value={jobManagerIds}
              options={managerFilterOptions}
              onChange={setJobManagerIds}
            />
          </div>
          <div className={styles.jobOptions}>
            {jobFilterOptions.length ? (
              jobFilterOptions.map((option) => (
                <Menu.Item
                  key={option.value}
                  isSelected={option.value === value}
                  onClick={() => {
                    onJobFilterChange(option.value);
                    setSelectedJobTitle(option.label);
                    close();
                    setSearchKey('');
                    setJobManagerIds([]);
                  }}
                >
                  {option.label}
                </Menu.Item>
              ))
            ) : (
              <div className={styles.nodata}>{i18n.t`暂无数据`}</div>
            )}
          </div>
        </div>
      )}
      onChange={(_value) => {
        onJobFilterChange(_value);
        setSelectedJobTitle('');
        setSearchKey('');
        setJobManagerIds([]);
      }}
    />
  );
};

export default JobFilter;
